// Mirrors the response shape produced by serializeTenant / serializeUser
// in new-backend/admin/src/controllers/accountController.ts.

export type TenantSolution = 'room' | 'key' | 'station' | 'sup';
export type TenantPlan = 'trial' | 'starter' | 'professional' | 'enterprise';
// German salutation for customer emails. Orthogonal to the DE/EN split — it
// only selects which DE variant of a bilingual template renders. Default `sie`
// (existing room-rental tone). See #1642.
export type TenantAnrede = 'du' | 'sie';
export type TenantStatus = 'onboarding' | 'active' | 'suspended' | 'archived';
export type UserRole = 'owner' | 'manager' | 'service';
// Legal-entity category. Drives which legal-notice fields apply: only
// `company` carries a trade register (HRB/HRA + register court). Backend
// enum `tenant_organization_type_enum`; defaults to `company` for existing
// tenants. See #951.
export type TenantOrganizationType = 'company' | 'municipality' | 'association';

// Sub-objects extracted from `tenant.config` JSONB and surfaced as typed
// fields by the backend's `serializeTenant`. All three are `null` until
// the matching section on the AccountPage has been saved.
export interface TenantBank {
    accountHolder: string;
    iban: string;
    bic: string;
    bankName: string;
}

export interface TenantContact {
    email: string;
    phone: string;
    /** Optional public homepage URL (e.g. used for terms links). */
    website?: string | null;
}

// Branding for the shared end-user booking page (app.kolula.com/{slug}).
// Colors are CSS-ready RGB triplet strings (e.g. "0, 84, 117"); logo/favicon
// are public asset URLs. All optional until the tenant uploads them.
export interface TenantBranding {
    primaryColor?: string;
    onPrimaryColor?: string;
    logoUrl?: string;
    faviconUrl?: string;
}

// Generic check-in / check-out flow config. Producer side of the block the
// end-user app consumes (apps/end-user/src/types/tenantConfig.ts —
// `CheckInCheckOutFlowConfig`). Kept in sync so admin (producer) and
// end-user (consumer) agree on the shape persisted to tenant.config.

/** Bilingual copy entry — DE/EN, mirroring the asset/group langNames convention. */
export interface LocalizedFlowText {
    de?: string;
    en?: string;
}

export interface TenantUseCaseFlowConfig {
    /** Opt-in for THIS use case: when true, the generic flow replaces the
     *  legacy room flow for bookings of this use case. */
    enabled: boolean;
    /** Which steps render — the screen selector. A short flow for one use case
     *  and a longer one for another is this map differing between entries. */
    modules: {
        welcomeInfo?: boolean;
        /** Mandatory presentation at check-in (no checkbox/signature). */
        houseRules?: boolean;
        assetReference?: boolean;
        startChecklist?: boolean;
        endChecklist?: boolean;
        checkOutPhoto?: boolean;
    };
    /**
     * Optional copy overrides for this use case; fall back to i18n defaults
     * when absent. Bilingual per entry ({ de, en } — same convention as
     * asset/group langNames). A bare string is the legacy single-language
     * shape from the old single-field editor (loaded into the DE field;
     * normalized to `{ de }` by the backend on the next save).
     */
    texts?: {
        welcome?: LocalizedFlowText | string;
        houseRules?: LocalizedFlowText | string;
        startConfirmation?: LocalizedFlowText | string;
        endConfirmation?: LocalizedFlowText | string;
    };
    /**
     * Authored checklist content for this use case. Absent means the end-user
     * app uses its per-use-case i18n defaults — the state every tenant is in
     * until they edit one, and deliberately different from an authored empty
     * item list, which is the lump-sum checklist.
     */
    checklists?: {
        start?: TenantChecklistConfig;
        end?: TenantChecklistConfig;
    };
}

/** One authored checklist item. `key` is stable and opaque: the end-user app
 *  keys its flag state on it, so re-wording or reordering must not change it. */
export interface TenantChecklistItemConfig {
    key: string;
    label: LocalizedFlowText;
}

/**
 * One authored checklist. **The item count is the mode** (PO, 2026-09-04): an
 * empty `items` renders the lump-sum screen — "look around, tell us if anything
 * is off" — and a non-empty one the itemised list. No mode flag, because two
 * ways to say the same thing is two ways for them to disagree.
 */
export interface TenantChecklistConfig {
    title?: LocalizedFlowText;
    description?: LocalizedFlowText;
    items: TenantChecklistItemConfig[];
}

export interface TenantCheckInCheckOutConfig {
    /**
     * The flow, per use case (PO, 2026-09-03). A use case with no entry has NO
     * flow — there is deliberately no tenant-level default to fall back to.
     * Mirror of `CheckInCheckOutConfig` in both new backends and
     * `CheckInCheckOutFlowConfig` in the end-user app; see
     * `docs/plans/per-use-case-check-in-check-out-configuration.md` §3.
     */
    useCases?: Record<string, TenantUseCaseFlowConfig>;
    /**
     * Decision 2 — access management / digital lock add-on, layered on top of
     * the usage flow (Decision 1, `enabled`). Orthogonal sub add-on gated by the
     * `accessManagement` entitlement. When `enabled`, the end-user flow gains the
     * hardware modules (door open at check-in / check-out). `locationCheck`
     * optionally requires QR/location verification before the door opens.
     */
    accessManagement?: {
        enabled: boolean;
        locationCheck?: boolean;
    };
    /**
     * Pre-start email series (Types 12–13, #1639) — per-stage opt-in + how many
     * hours before the booking start each mail goes out. Absent ⇒ both stages
     * off at 48 h / 24 h.
     *
     * No editor yet: the write endpoint therefore PRESERVES a stored block when
     * the payload omits this key, so saving the flow section can't wipe it. Once
     * an editor exists it must send the block on every save.
     */
    preStartEmails?: {
        reminder: { enabled: boolean, offsetHours: number };
        welcome: { enabled: boolean, offsetHours: number };
    };
}

export interface TenantLegalNotice {
    street: string;
    postalCode: string;
    city: string;
    managingDirector: string;
    /** Register type + number (e.g. `HRB 12345`). Stored verbatim. */
    hrb: string | null;
    /** Issuing court (e.g. `Amtsgericht München`). Joined with `hrb` via
     *  ", " when rendered (#886). */
    hrbCourt: string | null;
    /** Chair of the supervisory board, printed on the invoice. Absent on tenants saved before it existed. */
    supervisoryBoardChair?: string | null;
    vatId: string | null;
    /** Steuernummer. Absent on tenants saved before the field existed. */
    taxNumber?: string | null;
}

export interface AccountTenant {
    id: string;
    organizationName: string;
    organizationType: TenantOrganizationType;
    slug: string | null;
    country: string;
    /**
     * #1353 §19 UStG. When true the tenant charges no VAT and its invoices
     * carry the exemption note instead of a tax line.
     */
    isKleinunternehmer: boolean;
    solution: TenantSolution;
    plan: TenantPlan;
    status: TenantStatus;
    /** #1379: when the owner closed the account; null while the tenant is live. */
    archivedAt: string | null;
    /** #1379: when the data is deleted and reactivation ends; null while live. */
    deletionDueAt: string | null;
    maxObjects: number;
    maxUsers: number;
    trialExpiresAt: string | null;
    createdAt: string;
    bank: TenantBank | null;
    /** #1338 — null until the tenant configures payments. */
    payments: TenantPayments | null;
    /** End-user "ideas" home card content (per language). null = card off.
     *  Mirrors the backend's MoreToComeTexts shape; toggled + edited in
     *  Services → Use cases. */
    moreToCome: Partial<Record<'de' | 'en', MoreToComeTexts>> | null;
    /** The season organisation owners may book series in. null = unset, which
     *  refuses end-user series. Edited in Services → Use cases. */
    seriesSeason?: SeriesSeason | null;
    /** The German state whose holidays apply (M9, #3041), e.g. `DE-BW`.
     *  null = unset, which means no holidays at all. Services → Use cases. */
    holidayRegion?: string | null;
    /** How prices are typed in the admin (#2967): `net` converts to gross on
     *  save; absent/`gross` = as today. Storage is gross either way. Edited in
     *  Services → Preise. */
    priceEntry?: PriceEntryMode;
    /** ADR-0012 WP0 — who issues the statutory invoice. `external` = the
     *  tenant's own ERP/billing system (Mode C); `kolula` = kolula issues
     *  (Mode A — storable since 2026-08-19 via its preliminary stage). null =
     *  unconfigured, which keeps the invoice channel locked in Services. */
    invoiceIssuer: TenantInvoiceIssuer | null;
    /** Where issued invoices are mailed; null = the booking's customer. */
    invoiceDispatchEmail?: string | null;
    /** #3427: same-rate extras are folded into the stay line on issued invoices. */
    invoiceBundleExtrasIntoStay?: boolean;
    /** #1353 phase 4 — kolula issues this tenant's invoices as ZUGFeRD
     *  (PDF/A-3 with EN 16931 data). null = plain PDF. Set by kolula only. */
    /** The payment provider the tenant WANTS — a wish, never a status (the
     *  connection is derived live via payment-integration). null = unchosen. */
    desiredPaymentProvider: TenantDesiredPaymentProvider | null;
    contact: TenantContact | null;
    legalNotice: TenantLegalNotice | null;
    /** Tenant overrides for the postpay legal texts (#1703). null = none saved,
     *  so every mail renders kolula's default wording. */
    legalTexts: TenantLegalTexts | null;
    /** The tenant's own terms documents (upload or link each) and every earlier
     *  version. No documents = kolula's default applies. ADR-0003 amendment
     *  2026-10-02. */
    ownTerms?: TenantOwnTerms;
    /** The tenant's own FAQ questions, shown after kolula's fixed set
     *  (ADR-0003 amendment 2026-10-06). */
    faqs?: TenantFaqs;
    /** The data protection officer for the privacy policy; null = the tenant itself. */
    privacyOfficer?: TenantPrivacyOfficer | null;
    /** The booking page's own browser-tab title and description. */
    seo?: TenantSeo;
    branding: TenantBranding | null;
    /** Use cases the tenant offers (source of truth for end-user cards + admin
     *  create gating). Null until set on the tenant's config; the admin UI then
     *  falls back to the solution→services mapping. */
    services: string[] | null;
    /** Whether the tenant has CHOSEN its use cases (Account → Use cases), or
     *  `services` above is still the solution-derived fallback. The fallback
     *  keeps resolving so booking surfaces work, but it is shown as "derived,
     *  not yet confirmed" rather than passing silently (plan, phase 2). */
    servicesConfigured: boolean;
    /** Whether the booking page on the shared deployment is live. */
    endUserAppActive: boolean;
    /** German salutation for customer emails (du / Sie). Always present —
     *  the backend resolves it, defaulting to `sie`. See #1642. */
    anrede: TenantAnrede;
    /** Interim per-tenant "uses payment" signal (#1335 §3), from operational
     *  config. `null` when unset ⇒ admin-ui defaults `usesPaymentCodes` to true.
     *  #1338 replaces this with a derived `paymentMode`. */
    usesPayment: boolean | null;
    /** Generic check-in/check-out flow config (tenant-owned). Optional —
     *  absent until the tenant saves the section at least once. */
    checkInCheckOutFlow?: TenantCheckInCheckOutConfig;
    /** Per-tenant capability flags surfaced through getAccount. Provisional:
     *  the gating model (entitlement vs plan-tier vs service) is still under
     *  discussion — see useCanUseCheckInCheckOut. */
    entitlements?: {
        checkInCheckOut?: boolean;
        /** Access management / digital lock add-on (Decision 2). Resolved by the
         *  same union resolver as checkInCheckOut (solution implies it OR the
         *  add-on grants it). See useCanUseAccessManagement. */
        accessManagement?: boolean;
    };
}

// One row of the master tenant-switcher directory. Mirrors
// serializeTenantDirectoryEntry in new-backend/admin's accountController.
// `slug` is non-nullable here (unlike AccountTenant): the backend only
// returns tenants that have a slug, since the switcher routes by it — active
// ones and, since #1379, archived ones (marked, so support can reactivate).
export interface TenantDirectoryEntry {
    id: string;
    organizationName: string;
    slug: string;
    solution: TenantSolution;
    country: string;
    status: TenantStatus;
    archivedAt: string | null;
}

/**
 * kolula's contract tiers with a tenant (`tenant_contract_tier`, ADR-0001 Amendment 6).
 * Sibling: `shared/src/objectLimit.ts` `CONTRACT_TIERS`; pinned by `contractTiersParity.test.ts`.
 */
export type ContractTier = 'up_to_5' | 'up_to_15' | 'up_to_30' | 'individual';
export const CONTRACT_TIERS: readonly ContractTier[] = ['up_to_5', 'up_to_15', 'up_to_30', 'individual'];

/**
 * The largest `objectLimit` the server accepts: `tenant_contract.object_limit`
 * is `INTEGER`. Sibling: `shared/src/objectLimit.ts` `OBJECT_LIMIT_MAX`
 * (`isObjectLimitValue`); pinned by `contractTiersParity.test.ts`.
 */
export const OBJECT_LIMIT_MAX = 2_147_483_647;

/** The current contract as `GET /admin/master/tenants` returns it. Dates `YYYY-MM-DD`. */
export interface MasterTenantContract {
    id: string;
    tier: ContractTier;
    offerNumber: string;
    acceptedOn: string;
    startsOn: string;
    note: string | null;
    /** `individual` only: rooms + stations it allows; null = "Unbegrenzt" (always null for a standard tier). */
    objectLimit: number | null;
    createdAt: string | null;
}

/**
 * One row of the master page "Mandanten" — `serializeMasterTenant` in
 * new-backend/admin's `masterTenantsController.ts`. Also the answer of
 * `POST /admin/master/tenants/:slug/contract`.
 */
export interface MasterTenantRow {
    id: string;
    organizationName: string;
    slug: string;
    organizationType: TenantOrganizationType;
    solution: TenantSolution;
    status: TenantStatus;
    plan: TenantPlan;
    isTrial: boolean;
    createdAt: string | null;
    trialExpiresAt: string | null;
    owner: { name: string | null, email: string | null } | null;
    contract: MasterTenantContract | null;
    counts: { rooms: number, stations: number };
    /** The limit applied today; null when none is. */
    limit: { max: number, basis: 'trial' | 'contract' } | null;
}

/**
 * `POST /admin/master/tenants/:slug/contract`. `startsOn` omitted = `acceptedOn`.
 * `objectLimit` is sent for `individual` only, and there always: a whole number
 * of at least 1, or null for "Unbegrenzt" (the server refuses it absent, and
 * refuses it for a standard tier).
 */
export interface EnterTenantContractRequest {
    slug: string;
    tier: ContractTier;
    objectLimit?: number | null;
    offerNumber: string;
    acceptedOn: string;
    startsOn?: string;
    note?: string;
}

// Company / legal / bank subset returned by
// GET /admin/tenants/:slug/invoice-info (master-only). Mirrors the matching
// AccountTenant fields so a single mapper can build the invoice config from
// either this or the owner's own getAccount response.
export interface TenantInvoiceInfo {
    organizationName: string;
    country: string;
    legalNotice: TenantLegalNotice | null;
    bank: TenantBank | null;
    /** Uploaded branding (logo) so master-generated invoices carry the
     * tenant's own logo, mirroring `AccountTenant.branding`. */
    branding: TenantBranding | null;
}

// GET /admin/tenants/:slug/account (master-only) — the viewed tenant's full
// account for a read-only master view. No `user` (the master isn't a member of
// that tenant), so the page hides the personal section and renders read-only.
export interface TenantAccountResponse {
    tenant: AccountTenant;
}

export interface AccountUser {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    role: UserRole;
}

// POST /admin/account/users — create a colleague on the caller's tenant. Any
// tenant role (owner/manager/service) may be assigned. Backend is Auth0-backed
// and not implemented yet (the frontend ships against this documented contract;
// the endpoint follows in #1593).
export interface CreateTenantUserRequest {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    role: UserRole;
}

// PATCH /admin/account/users/:id — edit a colleague. Email is the Auth0
// identity and is not editable, so it's omitted. Backend is the same
// Auth0-backed follow-up (#1593).
export interface UpdateTenantUserRequest {
    firstName: string;
    lastName: string;
    phone?: string;
    role: UserRole;
}

// Shared response shape for getAccount + every PATCH section endpoint.
export interface AccountResponse {
    tenant: AccountTenant;
    user: AccountUser;
}

// GET /admin/account/permissions — the caller's DB role + the admin-UI
// permission strings derived from it (#1377). Replaces the former hardcoded
// SELF_SERVICE_OWNER_PERMISSIONS frontend constant; the backend
// (new-backend/admin/src/lib/rolePermissions.ts) is the single source of truth.
export interface AccountPermissionsResponse {
    role: UserRole;
    permissions: string[];
}

export interface RegisterTenantRequest {
    firstName: string;
    lastName: string;
    organizationName: string;
    /** Required during onboarding — backend rejects missing/invalid with
     *  400 (#951). */
    organizationType: TenantOrganizationType;
    country: string;
    /** User-chosen tenant slug. Becomes the X-Tenant header for every
     *  subsequent request from this user. Validated server-side against
     *  SLUG_REGEX and a reserved-slugs blocklist. Lowercased. */
    slug: string;
    phone?: string;
    solution?: TenantSolution;
    /** Auth0 access tokens don't include email by default per #740, so the
     *  frontend forwards user.email from the ID token. */
    email?: string;
}

export interface RegisterTenantResponse {
    tenant: AccountTenant;
    user: AccountUser;
    existing: boolean;
}

export type GetAccountResponse =
    | { onboardingPending: true }
    | AccountResponse;

export interface UpdateProfileRequest {
    firstName: string;
    lastName: string;
    phone?: string;
}

export interface UpdateOrganizationRequest {
    organizationName: string;
    country: string;
    /** Optional on this endpoint — backend keeps the existing value when
     *  omitted (#990). */
    organizationType?: TenantOrganizationType;
    /**
     * #1353 §19 UStG. Optional, and only a boolean is honoured server-side:
     * this endpoint is also the plain "rename the organization" save, so an
     * absent field must leave the flag alone.
     */
    isKleinunternehmer?: boolean;
}

// PATCH /admin/account/bank-details — bank section only. Public contact has
// its own endpoint (UpdateContactRequest) so the two account sections save
// independently.
export interface UpdateBankDetailsRequest {
    accountHolder: string;
    iban: string;
    bic: string;
    bankName: string;
}

// PATCH /admin/account/contact — public contact info only.
export interface UpdateContactRequest {
    contactEmail: string;
    contactPhone: string;
    /** Optional public homepage URL. Omit (undefined) to clear it. */
    website?: string;
}

export interface UpdateLegalNoticeRequest {
    street: string;
    postalCode: string;
    city: string;
    managingDirector: string;
    /** Optional on this endpoint — backend keeps the existing value when
     *  omitted (#951). */
    organizationType?: TenantOrganizationType;
    hrb?: string;
    hrbCourt?: string;
    supervisoryBoardChair?: string;
    vatId?: string;
    taxNumber?: string;
}

// PATCH /admin/account/booking-page — toggles tenant.config.endUserAppActive.
export interface UpdateBookingPageRequest {
    active: boolean;
}

// PATCH /admin/account/anrede — writes tenant.config.anrede (du / Sie), the
// German salutation for customer emails (#1642).
export interface UpdateAnredeRequest {
    anrede: TenantAnrede;
}

// PATCH /admin/account/branding — multipart. Each field is optional; only the
// provided ones overwrite their branding key (others are preserved server-side).
// `primaryColor` is sent as a hex string ("#RRGGBB"); the backend normalizes it
// into the CSS-ready "r, g, b" triplet stored in BrandingConfig.
export interface UpdateBrandingRequest {
    primaryColor?: string;
    logo?: File;
    favicon?: File;
}

// PATCH /admin/account/check-in-checkout — writes tenant.config.checkInCheckOutFlow.
// Full config block from form state (enabled + modules + optional texts).
export type UpdateCheckInCheckOutConfigRequest = TenantCheckInCheckOutConfig;

// Constant-response shape from POST /admin/account/resend-verification.
// The backend always returns 200 with this body regardless of outcome
// (rate-limited, already verified, user missing, etc.) — see #954. Only
// network errors surface as `error` on the RTK Query result.
export interface ResendVerificationResponse {
    message: string;
}

/**
 * POST /admin/account/deletion-request (#1379 interim, owner only). 200 only
 * once both the request to kolula support and the owner's confirmation mail
 * went out; every other outcome is an error with a `code`.
 */
export interface AccountDeletionRequestBody {
    /** UI language for the confirmation mail; German otherwise. */
    language: 'de' | 'en';
}

export interface AccountDeletionRequestResponse {
    requestedAt: string;
}

/** GET /admin/account/cancellation (#1379, owner only): what the "Cancel
 *  account" dialog shows before the owner confirms. */
export interface AccountCancellationResponse {
    /** Bookings not cancelled and not yet ended; any of them blocks the cancel. */
    upcomingBookings: number;
    retentionDays: number;
}

/** POST /admin/account/cancel (#1379, owner only). The tenant is archived. */
export interface AccountCancelResponse {
    archivedAt: string;
    deletionDueAt: string;
    alreadyArchived: boolean;
    mails?: { support: 'sent' | 'failed', owner: 'sent' | 'failed' };
}

/** #1338 / ADR-0016 Amendment 4 — tenant service configuration. Absent until saved.
 *  Mirrors `new-backend/admin/src/lib/tenantConfig.ts`; the two cannot import
 *  each other (separate deploys), so they name each other. */
export type TenantBookingFlow = 'book_now' | 'reserve_pay_first' | 'reserve_accept_postpay';

/** The timing choice on the reservation path — the only part of the flow that
 *  is still stored. `book_now` is not a third value here but a capability
 *  switched on top of them (`directBooking`): a reservation always exists,
 *  because a phone or e-mail request is recorded in the admin area and that
 *  path cannot be switched off. */
export type TenantReservationFlow = 'reserve_pay_first' | 'reserve_accept_postpay';

/** Does the offer cost anything — the plan's Q2, asked before any payment
 *  question because `free` ends the configuration outright. Declared by the
 *  tenant rather than derived from whether price rows exist: otherwise an
 *  admin typing a price turns a service meant to be free into a chargeable
 *  one, silently. */
export type TenantChargingMode = 'free' | 'mixed' | 'chargeable';

/** How the customer pays. Replaces the former per-tenant payment mode: the
 *  choice is per offer, because one tenant runs several at once. */
export type TenantPaymentChannel =
    | 'provider_checkout'
    | 'provider_link'
    | 'bank_transfer'
    | 'invoice'
    | 'no_payment';

export type TenantDetectionCapability = 'unmonitored' | 'monitored' | 'adapter';
export type TenantPaymentExpiryReaction = 'notify' | 'cancel';

export interface TenantPaymentsArrangement {
    flow: TenantReservationFlow;
    channels: TenantPaymentChannel[];
}

/** One use case's configuration — the three questions in the order they are
 *  asked, each one removing questions below it. */
export interface TenantServiceConfig {
    /** Q1 — reservation always exists; whether the WEB APP additionally allows
     *  direct booking. Additive: the reservation request stays on offer for
     *  what direct booking cannot serve. */
    directBooking: boolean;
    /** Q2 — free ends the configuration. */
    charging: TenantChargingMode;
    /** Set when the backend stored our template on switch-on, never by the
     *  tenant's own save — the entry still reads as "not yet saved"
     *  (`isSavedServiceEntry`). Nothing else reads it. */
    seededFromTemplate?: true;
    /** Q3 — absent exactly when `charging === 'free'`. */
    payment?: TenantPaymentsArrangement;
    /**
     * The tenant's own event-type vocabulary for this use case — one
     * language-neutral list, rendered verbatim in both UI languages
     * (`docs/plans/tenant-configurable-event-types.md`).
     *
     * ABSENT = the standard list, which is what every unconfigured tenant and
     * every legacy tenant gets. Never stored empty — "use the standard list" is
     * expressed by omitting the key, because an empty list would render a
     * dropdown with no options. Read it through `useEventTypeOptions`, never
     * directly, so absence cannot be mistaken for a configured emptiness.
     */
    eventTypes?: string[];
    /**
     * Event types this use case used to offer. SERVER-OWNED: recomputed on
     * every save from what was stored, so the form neither maintains nor sends
     * it. Read-only here — it feeds the "put it back" offers in the editor, and
     * it is what keeps a tenant's own idea from being erased by a click on the
     * bin.
     */
    eventTypesRetired?: string[];
    /**
     * WHEN a customer may book this use case — the lead buffer, the horizon and
     * the follow-up buffer, all as STORED (minutes, and whole months for the
     * horizon). The editor converts to the unit the tenant picks in;
     * `pages/services/bookingWindowRules.ts` owns both.
     *
     * ABSENT = the documented defaults, which reproduce exactly what the
     * end-user screens hardcoded before this setting existed — five days'
     * notice and an eighteen-month horizon on `apartment` and `venue`, none and
     * one month elsewhere, no follow-up buffer anywhere.
     *
     * The buffers apply to CUSTOMER bookings only. A booking staff create in
     * the admin area is deliberately not subject to them (PO, 2026-09-20).
     */
    bookingWindow?: TenantBookingWindow;
    /**
     * M10 (#3042): the standard category's cancellation rule, inherited by
     * every further category without its own. ABSENT = no rule, the AGB
     * governs. Validated by `new-backend/admin/src/lib/booking/cancellationRule.ts`.
     */
    cancellation?: TenantCancellationRule;
}

/** One cancellation fee tier: from `daysBefore` calendar days before the start, `percent` of the price. */
export interface TenantCancellationTier {
    daysBefore: number;
    percent: number;
}

/** Free until the largest `daysBefore`, then the tiers. `tiers: []` = free until the start. */
export interface TenantCancellationRule {
    tiers: TenantCancellationTier[];
    /** The tenant confirmed the rule matches its terms / fee schedule. */
    agbConfirmed: true;
}

/** One use case's booking window, as stored. */
export interface TenantBookingWindow {
    /** Minimum notice before a booking may start, in minutes. */
    leadMinutes: number;
    /** Latest bookable date, in whole months ahead (end of that month). */
    horizonMonths: number;
    /** How long a unit rests after a booking ends, in minutes. */
    followUpMinutes: number;
    /** Shortest stay, in nights — apartments only. Optional when stored:
     *  absent is one night. */
    minNights?: number;
}

export interface TenantPaymentsPrepay {
    deadlineDays: number;
    latestWorkingDaysBeforeStart: number;
    reminderDaysBefore: number;
    onExpiry: TenantPaymentExpiryReaction;
}

/** The block behind the Services area. The stored key stays `payments` although
 *  it now also carries booking logic and charging — nothing consumes it, so a
 *  rename would be free, but it would be a second change riding along. */
export interface TenantPayments {
    useCases: Record<string, TenantServiceConfig>;
    prepay: TenantPaymentsPrepay;
    /** Postpay terms (#2318). Optional, and each field nullable: absence is the
     *  documented rule, not an unset value — no validity window means the offer
     *  runs until the slot starts (D8), and no payment term means 14 days. */
    postpay?: TenantPaymentsPostpay;
    detection: TenantDetectionCapability;
}

/** The two dates an offer carries — how long to accept, how long to pay. */
export interface TenantPaymentsPostpay {
    offerValidityDays?: number | null;
    paymentTermDays?: number | null;
}

/** GET /admin/account/payment-integration — derived, never configured here. */
export type TenantStripeConnectionStatus =
    | 'connected'
    | 'shared_credentials'
    | 'not_connected';

/** Identity of the Stripe account behind the deposited key — read live from
 *  Stripe, only for `stripe: 'connected'`; null when the read failed. */
export interface TenantStripeAccountIdentity {
    name: string | null;
    maskedId: string;
}

export interface TenantPaymentIntegration {
    /** Optional: older backends don't send it; a failed live read is null. */
    account?: TenantStripeAccountIdentity | null;
    stripe: TenantStripeConnectionStatus;
    connectAccount: boolean;
}

/** PATCH /admin/account/payments — replaces the whole block. */
/** `useCases` omitted = keep the stored ones: each use case's booking fields
 *  are its standard product category's, saved through that category's own
 *  endpoint (ADR-0026 §AF). */
export type UpdatePaymentsConfigRequest = Omit<TenantPayments, 'useCases'>
    & Partial<Pick<TenantPayments, 'useCases'>>;

/** Page texts of the end-user "ideas" card, per language. {email} in
 *  second_text resolves to the tenant contact e-mail in the app. */
export interface MoreToComeTexts {
    header_text?: string;
    first_text?: string;
    second_text?: string;
    /** Home-card label override; must fit one line at mobile width. */
    alternative_title?: string;
}

/** PATCH /admin/account/more-to-come — enabled:false removes the block
 *  (card off); enabled:true replaces it with `texts`. */
/** The season organisations may book series in (`tenant.config.seriesSeason`). */
export interface SeriesSeason {
    /** YYYY-MM-DD, inclusive. */
    startDate: string;
    /** YYYY-MM-DD, inclusive. */
    endDate: string;
}

/** PATCH /admin/account/holiday-region — null removes it (no holidays). */
export interface UpdateHolidayRegionRequest {
    holidayRegion: string | null;
    /** Master viewing another tenant: write that tenant by slug. */
    slug?: string;
}

/** GET /admin/holidays/status — the tenant's region and the holiday data. */
export interface HolidayStatus {
    region: string | null;
    regionProblem: 'not_configured' | 'malformed' | null;
    /** The regions on offer: the 16 German states. */
    regions: string[];
    /** null on a store without holiday data (legacy). */
    data: {
        dataset: string | null;
        importedAt: string | null;
        coversUntil: string | null;
        rows: number;
    } | null;
    credit: { name: string, url: string, licence: string, licenceUrl: string };
}

/** `GET /admin/holidays?from&to`: only the dates that have a holiday. */
export interface HolidaysInRange {
    /** null = no region set (or malformed): `days` is then empty. */
    region: string | null;
    regionProblem: 'not_configured' | 'malformed' | null;
    days: Array<{
        date: string;
        /** Public before school. */
        holidays: Array<{ kind: 'public' | 'school', name: { language: string, text: string }[] }>;
    }>;
}

export interface UpdateSeriesSeasonRequest {
    startDate: string | null;
    endDate: string | null;
    /** Master viewing another tenant: write that tenant by slug. */
    slug?: string;
}

export interface UpdateMoreToComeRequest {
    enabled: boolean;
    texts?: Partial<Record<'de' | 'en', MoreToComeTexts>>;
}

/** ADR-0012's two issuance modes. */
export type TenantInvoiceIssuer = 'kolula' | 'external';

// PATCH /admin/account/invoice-issuer (own tenant) and
// PATCH /admin/tenants/:slug/invoice-issuer (master, by slug) — ADR-0012 WP0.
// Both modes accepted since 2026-08-19 (kolula = preliminary stage); null clears.
/**
 * The postpay legal texts a tenant may replace — the D1 package (#1703).
 *
 * Per language, because the mails render DE and EN side by side and a tenant
 * may want to replace one without the other. `null` on a field means the
 * tenant saved none, so kolula's default is used; a cleared field restores the
 * default rather than emptying the text, since each of these is a contract
 * term.
 *
 * The CTA label is deliberately absent: "Kostenpflichtig buchen" / "Book for a
 * fee" is the § 312j BGB button wording, not a matter of taste.
 */
/** The booking page's own browser-tab title and description; null = derived from the tenant's name. */
export interface TenantSeo {
    title: string | null;
    description: string | null;
}

/** The data protection officer named on the booking page's privacy policy. */
export interface TenantPrivacyOfficer {
    /** The firm acting as officer — required on save; null on entries from
     *  before 2026-10-12, which had one combined line in `name`. */
    company: string | null;
    /** The contact person — optional. */
    name: string | null;
    street: string | null;
    postalCode: string | null;
    city: string | null;
    email: string | null;
    phone: string | null;
}

/** One of the tenant's own terms documents — an uploaded PDF or a link. */
export interface TenantOwnTermsDocument {
    id: string;
    /** What customers see on the button, e.g. "AGB Gästewohnungen". */
    title: string;
    kind: 'upload' | 'link';
    url: string;
    /** The uploaded file's original name; null for a link. */
    fileName: string | null;
    /** ISO timestamp from which this version applies. */
    since: string;
}

export interface TenantOwnTerms {
    /** In force, in the order customers see them. */
    documents: TenantOwnTermsDocument[];
    /** Every earlier version, with the date it stopped applying. */
    history: (TenantOwnTermsDocument & { until: string })[];
}

/** One text per language; null where the tenant wrote nothing. */
export interface TenantFaqText {
    de: string | null;
    en: string | null;
}

/** One of the tenant's own FAQ questions. A language counts only with both
 *  its question and its answer. */
export interface TenantFaqItem {
    id: string;
    question: TenantFaqText;
    answer: TenantFaqText;
}

/** Tenant-wide questions, and per product-category key. */
export interface TenantFaqs {
    general: TenantFaqItem[];
    categories: Record<string, TenantFaqItem[]>;
}

/** New content for a terms document: a PDF file or a link. */
export type OwnTermsContentRequest = { file: File } | { url: string };

export interface TenantLegalTexts {
    paymentTermsDe?: string | null;
    paymentTermsEn?: string | null;
    noWithdrawalDe?: string | null;
    noWithdrawalEn?: string | null;
    agbNoteDe?: string | null;
    agbNoteEn?: string | null;
}

export type UpdateLegalTextsRequest = TenantLegalTexts;

export interface UpdateInvoiceIssuerRequest {
    invoiceIssuer: TenantInvoiceIssuer | null;
    /** Master path: the tenant to write, by slug. Omitted = caller's own. */
    slug?: string;
}

/** The providers a tenant can wish for. Only Stripe exists today. */
export type TenantDesiredPaymentProvider = 'stripe';

// PATCH /admin/account/payment-provider (own tenant) and
// PATCH /admin/tenants/:slug/payment-provider (master, by slug). A wish,
// never a status — the connection stays derived via payment-integration.
export interface UpdateDesiredPaymentProviderRequest {
    desiredPaymentProvider: TenantDesiredPaymentProvider | null;
    /** Master path: the tenant to write, by slug. Omitted = caller's own. */
    slug?: string;
}

// PATCH /admin/account/services — which use cases the tenant offers (the
// enable switch, plan phase 2). Server-side rules: only ENABLEABLE_SERVICES
// may be switched on (already-stored services may be kept), at least one, and
// a trial tenant gets exactly one.
export interface UpdateServicesRequest {
    services: string[];
}

/** How prices are typed in the admin (#2967, docs/plans/net-price-entry.md). */
export type PriceEntryMode = 'gross' | 'net';

/** PATCH /account/price-entry (own) or /tenants/:slug/price-entry (master). */
export interface UpdatePriceEntryRequest {
    priceEntry: PriceEntryMode;
    /** Set for a master writing a viewed tenant. */
    slug?: string;
}

// ── Onboarding import (docs/plans/onboarding-import.md) ─────────────────
// Wire shape of `new-backend/admin/src/lib/onboarding/onboardingImport.ts`
// (`OnboardingReport`); the file format itself lives in `@kolula/shared`.

export type OnboardingImportSection =
    | 'account' | 'customerClass' | 'organization' | 'product' | 'room' | 'price' | 'setting' | 'document'
    /**
     * Inside an organisation (the organisation import, plan docs/plans/organization-import.md):
     * a unit, a contact person, a room contract, a room of a contract, its fixed weekly times.
     * The onboarding import reports the first three as skipped (`organization_import_only`).
     */
    | 'unit' | 'contact' | 'contract' | 'access' | 'window'
    /** A "manuell anlegen" entry of the file, or a top-level key kolula does not know: only ever reported as skipped. */
    | 'manual' | 'file';
export type OnboardingImportAction = 'create' | 'update' | 'unchanged';

export interface OnboardingImportIssue {
    level: 'error' | 'warning';
    /** The page translates it, filled from `params`; `message` is the same in English. */
    code: string;
    field?: string;
    params?: Record<string, string | number>;
    message: string;
}

/** As the preview shows it: text, or null for no value. */
export type OnboardingImportChangeValue = string | null;

/** A price row as the preview shows it (times HH:MM, the class by name, null = for everyone). */
export interface OnboardingImportPriceRow {
    days: string | null;
    durationInMinutes: number | null;
    isFullDay: boolean | null;
    startTime: string | null;
    endTime: string | null;
    customerClass: string | null;
    price: number;
    additionalDaysPrice: number | null;
}

export interface OnboardingImportPriceChange {
    kind: 'create' | 'update' | 'remove';
    row: OnboardingImportPriceRow;
    before?: { price: number, additionalDaysPrice: number | null };
}

export interface OnboardingImportItem {
    section: OnboardingImportSection;
    key: string;
    action: OnboardingImportAction;
    issues: OnboardingImportIssue[];
    detail?: Record<string, string | number | boolean | null>;
    /** An update: each changed field, stored → file. */
    changes?: Array<{ field: string, label?: string, from: OnboardingImportChangeValue, to: OnboardingImportChangeValue }>;
    /** An existing room's price list: rows created, changed and removed ("wird entfernt"). */
    priceChanges?: OnboardingImportPriceChange[];
    /**
     * Not written: the item has an error, or depends on one that has (its
     * issues say which). The rest of the file is written (PO, 2026-10-30).
     */
    skipped?: true;
    /** A part of an organisation: the organisation, and the contract (and room) it belongs to. */
    parent?: { organization: string, contract?: string, room?: string };
    /** A room's fixed weekly times: each created, kept or removed ("wird entfernt"). Weekday 1 = Monday. */
    windowChanges?: OnboardingImportWindowChange[];
}

export interface OnboardingImportWindowChange {
    kind: 'create' | 'keep' | 'remove';
    weekday: number;
    startTime: string;
    endTime: string;
    /** "YYYY-MM-DD" */
    validFrom: string;
    validTo: string;
    releaseDates?: string[];
}

export interface OnboardingImportReport {
    written: boolean;
    /**
     * Why the file is not an onboarding file at all (not an object, no or
     * another `version`) — then nothing was checked and nothing is written. A
     * problem inside one item is that item's issue instead.
     */
    shapeProblems: Array<{ path: string, code: string, params?: Record<string, string | number>, message: string }>;
    items: OnboardingImportItem[];
    manual: Array<{ topic: string, note: string }>;
    /** "Manuell anlegen" the import adds itself, as codes the page translates. */
    notices?: Array<{ code: string, params?: Record<string, string | number> }>;
    errors: number;
    warnings: number;
    /** Apply only: the rooms created now, by the file's room name. */
    createdRooms?: Array<{ name: string, groupId: string, assetId: string }>;
    /**
     * Apply only: every room of the file the page uploads photos to — created
     * now or matched — keyed by the room's name exactly as the file writes it.
     */
    rooms?: Record<string, { groupId: string, assetId: string, created: boolean, hasCover: boolean }>;
    /**
     * Apply only: the Nutzungsordnung the page uploads next — a new version of
     * the stored document of that title (`documentId`), or a new document.
     */
    terms?: { title: string, documentId: string | null } | null;
}

export interface OnboardingImportFormat {
    schema: unknown;
    example: unknown;
    /** The constructed guest-apartment example (absent from an older backend). */
    exampleApartments?: unknown;
    prompt: string;
}

export interface OnboardingImportArgs {
    /** Set for a master writing to the tenant in view; absent for the owner's own account. */
    slug?: string;
    /** The JSON text as uploaded. */
    fileText: string;
    /** Photos per room name. Only their counts travel with the file; the files follow after the apply. */
    photos: Record<string, File[]>;
    /** The terms PDF; only whether one is attached travels with the file. */
    termsOfUse: File | null;
}
